import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { compose, withHandlers, withProps } from 'recompose'
import { bindActionCreators } from 'redux'
import DataTable from '../../app/components/dataTable'
import withDataTable, { sorts } from '../../app/components/dataTable/withDataTable'
import { exportPostingsUpdatesCSV, modalOpen } from '../../common/actions'
import { statesByCode } from '../../common/helpers'
import FileIcon from '../images/GovDocs-File-Icon.png'
import UpdateDetail from './updateDetail'
import { StyledExternalLink, StyledSpan } from '../../theme/Global'
import { openPdf } from '../../services/postingsUpdatesService'

const utc = require('dayjs/plugin/utc')

dayjs.extend(utc)

const columns = [
  {
    key: 'dateKey',
    sort: (a, b) => sorts.amount(b.id, a.id ),
    text: 'Update Summary',
    toolTip: 'Select to view pertinent information regarding this update\'s affected regions.',
    width: 1200
  },
  {
    key: 'mandatory',
    sort: null,
    text: 'Shipment Triggering Changes',
    toolTip:
      'Your locations in these states or provinces will automatically receive new posters as part of your Update Subscription. Shipment triggering city updates can be found by downloading the grid',
    width: 1800
  },
  {
    key: 'nonMandatory',
    sort: null,
    text: 'Other Changes',
    width: 1800
  },

  {
    key: 'theGrid',
    sort: null,
    text: 'The Grid',
    toolTip: 'Monthly summary of all labor law changes',
    width: 1000
  }
]

const UpdateTableRenderer = ({
  defaultSort,
  onExport,
  onFilter,
  onSort,
  recordCount,
  recordType,
  renderRow,
  showDateFilter
}) => (
  <DataTable
    columns={columns}
    data={renderRow}
    defaultSort={defaultSort}
    onExport={onExport}
    onFilter={onFilter}
    onSort={onSort}
    recordType={recordType}
    recordCount={recordCount}
    rowHeight={85}
    showDateFilter={showDateFilter}
  />
)

UpdateTableRenderer.propTypes = {
  defaultSort: PropTypes.object.isRequired,
  recordCount: PropTypes.number.isRequired,
  recordType: PropTypes.string.isRequired,
  renderRow: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onFilter: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  showDateFilter: PropTypes.bool.isRequired
}

UpdateTableRenderer.defaultProps = {}

const mapStateToProps = (state) => ({
  data: state.compliance.updates,
  companyId: state.account.company._id
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    exportPostingsUpdatesCSVDispatch: exportPostingsUpdatesCSV,
    modalOpenDispatch: modalOpen
  },
  dispatch
)

const UpdateTableComponent = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withHandlers({
    handleDateClick: (props) => (event) => {
      const update = props.data[event.target.dataset.id]
      const date = dayjs.utc(update.gridMonth).format('MMM YYYY')
      props.modalOpenDispatch({
        title: `Update Summary - ${date}`,
        component: <UpdateDetail data={update} />
      })
    }
  }),
  withProps((props) => ({
    defaultSort: {
      sortBy: 'dateKey',
      direction: 'DESC'
    },
    recordType: 'Updates'
  })),
  withDataTable({
    columns,
    filters: [
      // Search By Locale
      (item, filterData) => {
        if (filterData.locale) {
          if (item.mandatoryUpdates.includes(filterData.locale.state) === true) {
            return true
          }
          if (item.nonmandatoryUpdates.includes(filterData.locale.state) === true) {
            return true
          }
          return false
        }
        return true
      },
      // Search By Text
      (item, filterData) => {
        if (filterData.search) {
          let returnData = false
          const searchText = filterData.search.trim().toLowerCase()

          item.mandatoryUpdates.split(',').map((stateCode) => {
            if (stateCode.toLowerCase().indexOf(searchText) > -1) {
              returnData = true
              return
            }

            const state = statesByCode[stateCode]
            if (state && filterData.search.length > 2) {
              if (state.name.toLowerCase().indexOf(searchText) > -1) {
                returnData = true
              }
            }
          })

          item.nonmandatoryUpdates.split(',').map((stateCode) => {
            if (stateCode.toLowerCase().indexOf(searchText) > -1) {
              returnData = true
              return
            }

            const state = statesByCode[stateCode]
            if (state && filterData.search.length > 2) {
              if (state.name.toLowerCase().indexOf(searchText) > -1) {
                returnData = true
              }
            }
          })
          return returnData
        }
        return true
      }
    ],
    renderItem: (update, props) => ({
      dateKey: (
        <React.Fragment>
          <StyledExternalLink
            onClick={props.handleDateClick}
            data-id={update.id}
            style={{
              display: 'inline',
              fontSize: '12px'
            }}
          >
            {dayjs.utc(update.gridMonth).format('MMM YYYY')}
          </StyledExternalLink>
        </React.Fragment>
      ),
      mandatory: (<StyledSpan> {update.mandatoryUpdates.split(',').sort().join(', ')} </StyledSpan>),
      nonMandatory: (<StyledSpan> {update.nonmandatoryUpdates.split(',').sort().join(', ')} </StyledSpan>),
      theGrid: (
        <StyledExternalLink
          onClick={() => openPdf(update.s3BucketName, update.s3Key, update.region)}
        >
          <img
            alt="File download icon"
            src={FileIcon}
            style={{ maxWidth: '10%' }}
          />
        </StyledExternalLink>
      )
    }),
    showDateFilter: false
  }),
  withProps((props) => ({
    onExport: (exportType) => {
      let data = []
      props.filtered.map((key) => {
        const record = props.data[key]
        const row = []

        row.push(record.name)
        row.push(record.mandatoryUpdates)
        row.push(record.nonmandatoryUpdates)
        row.push(dayjs.utc(record.gridMonth).format('MM/DD/YYYY'))
        row.push(dayjs.utc(record.shipDate).format('MM/DD/YYYY'))
        row.push(dayjs.utc(record.estDeliveryDate).format('MM/DD/YYYY'))

        data.push(row)
      })

      // Currently sending more than 26k hits a size limit on the API gateway
      data = data.slice(0, 26000)

      props.exportPostingsUpdatesCSVDispatch({
        headers: [
          'Name',
          'Shipment Triggering Changes',
          'Non-Mandatory-Updates',
          'Update Date',
          'Ship Date',
          'Est. Deliver Date'
        ],
        data,
        filename: 'updates',
        params: { product: 'postingsUpdates' },
        companyId: props.companyId
      })
    }
  }))
)(UpdateTableRenderer)

export default UpdateTableComponent
